import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { getBlogPostBySlug } from '../data/blogData';
import { getTranslatedBlogContent } from '../data/blogTranslations';
import { getBlogFAQs } from '../data/blogFAQs';
import BlogFAQ from '../components/BlogFAQ';
import NotFound from 'shared/components/NotFound/NotFound';
import Seo from 'shared/seo/Seo';
import {
  buildArticleJsonLd,
  buildBreadcrumbJsonLd,
  buildFaqJsonLd,
} from 'shared/seo/jsonLd';
import { blogPostMeta, getCanonicalUrl } from 'shared/seo/seoMeta';

const blogSlugToMetaKey: Record<string, string> = {
  maintenance: 'maintenance',
  'maintenance-industrielle': 'maintenance',
  'water-treatment': 'waterTreatment',
  'traitement-des-eaux-usées-et-assainissement': 'waterTreatment',
  pools: 'pools',
  renovation: 'renovation',
  'de-ancien-au-moderne': 'renovation',
  'industrial-supply': 'industrialSupply',
  'fournitures-industrielles': 'industrialSupply',
  'air-compressors': 'airCompressors',
  'compresseurs-air': 'airCompressors',
  gmao: 'maintenanceGmao',
  'electrical-works': 'electricalWorks',
  'travaux-électricité': 'electricalWorks'
};

const BlogDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getBlogPostBySlug(slug) : null;
  const { t, i18n } = useTranslation();

  // Get translated content based on current language
  const translatedContent = useMemo(() => {
    if (!post) return null;
    return getTranslatedBlogContent(post.slug || '', i18n.language);
  }, [post, i18n.language]);

  // Used to redirect to /blog, which turned every bad slug into a soft 404 at
  // HTTP 200. Render the 404 in place so the URL carries its noindex.
  if (!post || post.status !== 'published') {
    return <NotFound />;
  }

  const formatDate = (dateString: string) => {
    const locale = i18n.language === 'ar' ? 'ar-SA' : i18n.language === 'en' ? 'en-US' : 'fr-FR';
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Use translated content if available, otherwise fall back to original
  const displayTitle = translatedContent?.title || post.title;
  const displayExcerpt = translatedContent?.excerpt || post.excerpt;
  const displayContent = translatedContent?.content || post.content;
  const isRTL = i18n.language === 'ar';
  const normalizedSlug = post.slug?.trim();
  const metaKey = blogSlugToMetaKey[normalizedSlug] || normalizedSlug;
  const meta = blogPostMeta[metaKey] || {
    title: `${displayTitle} - A2S SARL Blog`,
    description: displayExcerpt || '',
    canonicalPath: `/blog/${normalizedSlug}`
  };

  const canonical = getCanonicalUrl(meta.canonicalPath);
  // The same Q&A BlogFAQ renders below — Google requires the answers to be
  // visible on the page, so this is markup for real content, not hidden text.
  const faqs = getBlogFAQs(normalizedSlug, i18n.language);

  return (
    <>
      <Seo
        title={meta.title}
        description={meta.description}
        canonical={canonical}
        image={post.featuredImage}
        type="article"
        jsonLd={[
          buildArticleJsonLd({
            title: displayTitle,
            description: meta.description,
            canonical,
            image: post.featuredImage,
            datePublished: post.createdAt,
            dateModified: post.updatedAt,
          }),
          buildBreadcrumbJsonLd([
            { name: 'Accueil', path: '/' },
            { name: t('nav.blog', 'Blog'), path: '/blog' },
            { name: displayTitle, path: meta.canonicalPath },
          ]),
          ...(faqs.length ? [buildFaqJsonLd(faqs)] : []),
        ]}
      >
        <meta property="article:published_time" content={post.createdAt} />
        <meta property="article:modified_time" content={post.updatedAt} />
      </Seo>

      <Container
        dir={isRTL ? 'rtl' : 'ltr'}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        <BreadcrumbRow>
          <Breadcrumb>
            <BreadcrumbLink to="/blog">{t('nav.blog')}</BreadcrumbLink>
            <BreadcrumbSeparator>/</BreadcrumbSeparator>
            <BreadcrumbCurrent>{post.slug}</BreadcrumbCurrent>
          </Breadcrumb>
        </BreadcrumbRow>

        <HeaderImage>
          <img
            src={post.featuredImage || '/images/placeholder.jpg'}
            alt={displayTitle}
          />
        </HeaderImage>

       
          <Headline
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
          >
            {displayTitle}
          </Headline>

          <Meta
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.25 }}
          >
            <DateText>{formatDate(post.createdAt)}</DateText>
            <CategoryLabel>{t('blog.industry')}</CategoryLabel>
            <AuthorText>{t('blog.byA2S')}</AuthorText>
          </Meta>

          <SummaryCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.35 }}
          >
            <SummaryList>
              <li>{t('blog.keyPoints.point1')}</li>
              <li>{t('blog.keyPoints.point2')}</li>
              <li>{t('blog.keyPoints.point3')}</li>
            </SummaryList>
          </SummaryCard>
       

        <Content>
          <SummaryCard>

            <Article
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.45 }} dangerouslySetInnerHTML={{ __html: displayContent }}/>
        </SummaryCard>
          <Conclusion>
            <ConclusionTitle>{t('blog.whyA2S')}</ConclusionTitle>
            <ConclusionText>
              {t('blog.whyDescription')}
            </ConclusionText>
          </Conclusion>

          <ActionCard>
            <ActionText>{t('blog.readyToTransform')}</ActionText>
            <ActionButton to="/quote">{t('blog.requestQuote')}</ActionButton>
          </ActionCard>

<BlogFAQ slug={post.slug} />
        </Content>
      </Container>
    </>
  );
};

const Container = styled(motion.div)`
  max-width: var(--container-lg);
  margin: 0 auto;
  padding: var(--spacing-xxl) var(--spacing-lg);

  @media (max-width: 768px) {
    padding: var(--spacing-xl) var(--spacing-md);
  }
`;

const HeaderImage = styled.div`
  height: 400px;
  border-radius: var(--border-radius);
  overflow: hidden;
  margin-bottom: var(--spacing-xxl);
  box-shadow: var(--shadow-lg);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: 768px) {
    height: 250px;
    margin-bottom: var(--spacing-xl);
  }
`;

const Content = styled.div`
  
`;

const BreadcrumbRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-lg);
  flex-wrap: wrap;
`;

const Breadcrumb = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.95rem;
  color: var(--gray);
`;

const BreadcrumbLink = styled(Link)`
  color: var(--primary);
  text-decoration: none;
  font-weight: 600;

  &:hover {
    text-decoration: underline;
  }
`;

const BreadcrumbSeparator = styled.span`
  color: var(--gray);
  color: var(--text-color);
`;

const BreadcrumbCurrent = styled.span`
  color: var(--text-color);
  font-weight: 700;
  text-transform: lowercase;
`;

const BackLink = styled(Link)`
  color: var(--primary);
  background: var(--light);
  border: 1px solid var(--border-color);
  padding: 0.85rem 1.2rem;
  border-radius: var(--border-radius);
  text-decoration: none;
  font-size: 0.95rem;
  font-weight: 600;
  transition: var(--transition);

  &:hover {
    background: var(--primary);
    color: var(--white);
  }
`;

const IntroSection = styled.section`
  margin-bottom: var(--spacing-xxl);
`;

const Headline = styled(motion.h1)`
  font-size: clamp(2rem, 3.5vw, 3.2rem);
  font-weight: 800;
  color: var(--primary);
  line-height: 1.05;
  margin-bottom: var(--spacing-lg);
`;

const Meta = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: var(--spacing-lg);
  margin-bottom: var(--spacing-xxl);
  padding-bottom: var(--spacing-lg);
  border-bottom: 1px solid var(--border-color);
`;

const DateText = styled.span`
  color: var(--gray);
  font-size: 0.9rem;
`;

const AuthorText = styled.span`
  color: var(--primary);
  font-weight: 500;
  font-size: 0.9rem;
`;

const Article = styled(motion.div)`
  color: var(--text-color);
  line-height: 1.8;

  section {
    padding: 0;
    margin: 0 0 var(--spacing-xl);
  }

  section:not(:last-child) {
    padding-bottom: var(--spacing-lg);
    border-bottom: 1px solid var(--border-color);
  }

  h2 {
    font-size: clamp(1.55rem, 2vw, 1.9rem);
    font-weight: 800;
    color: var(--primary);
    line-height: 1.25;
    margin: 0 0 var(--spacing-md) 0;
    padding-inline-start: 0.85rem;
    border-inline-start: 4px solid var(--secondary);

    &:first-child {
      margin-top: 0;
    }
  }

  h3 {
    font-size: 1.4rem;
    font-weight: 600;
    color: var(--text-color);
    margin: var(--spacing-lg) 0 var(--spacing-md) 0;
  }

  p {
    margin-bottom: var(--spacing-md);
    font-size: 1.1rem;
    color: var(--text-color);

    @media (max-width: 768px) {
      font-size: 1rem;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  ul, ol {
    margin: var(--spacing-md) 0 0;
    padding-inline-start: 0;
    list-style: none;

    li {
      position: relative;
      margin-bottom: 0.75rem;
      padding-inline-start: 1.6rem;
      font-size: 1.1rem;
      line-height: 1.7;

      &::before {
        content: '';
        position: absolute;
        inset-inline-start: 0;
        top: 0.78em;
        width: 0.45rem;
        height: 0.45rem;
        border-radius: 50%;
        background: var(--secondary);
      }

      @media (max-width: 768px) {
        font-size: 1rem;
      }

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  a {
    color: var(--primary);
    font-weight: 700;
    text-decoration: none;
    border-bottom: 1px solid var(--secondary);
    transition: var(--transition);

    &:hover {
      color: var(--secondary);
    }
  }

  blockquote {
    border-inline-start: 4px solid var(--primary);
    padding-inline-start: var(--spacing-lg);
    margin: var(--spacing-lg) 0;
    font-style: italic;
    color: var(--gray);
  }

  img {
    max-width: 100%;
    height: auto;
    border-radius: var(--border-radius);
    margin: var(--spacing-lg) 0;
  }

  @media (max-width: 768px) {
    h2 {
      font-size: 1.5rem;
    }

    h3 {
      font-size: 1.2rem;
    }
  }
`;

const SummaryCard = styled(motion.div)`
  background: var(--white);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  padding: var(--spacing-xl);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.06);
  margin-top: var(--spacing-lg);

  @media (max-width: 768px) {
    padding: var(--spacing-lg);
  }
`;

const SummaryTitle = styled.h2`
  font-size: 1.4rem;
  font-weight: 700;
  color: var(--primary);
  margin-bottom: var(--spacing-md);
`;

const SummaryList = styled.ul`
  list-style: disc inside;
  color: var(--text-color);
  font-size: 1rem;
  line-height: 1.8;

  li {
    margin-bottom: var(--spacing-sm);
  }
`;

const CategoryLabel = styled.span`
  display: inline-flex;
  padding: 0.4rem 0.85rem;
  border-radius: 999px;
  background: var(--light);
  color: var(--primary);
  font-size: 0.9rem;
  font-weight: 700;
`;

const Conclusion = styled.section`
  margin-top: var(--spacing-xxl);
  padding: var(--spacing-xl);
  border-radius: var(--border-radius);
  background: var(--secondary-light);
  border: 1px solid var(--border-color);
`;

const ConclusionTitle = styled.h2`
  font-size: 1.6rem;
  font-weight: 700;
  margin-bottom: var(--spacing-md);
  color: var(--primary);
`;

const ConclusionText = styled.p`
  font-size: 1rem;
  line-height: 1.75;
  color: var(--text-color);
`;

const ActionCard = styled.div`
  margin-top: var(--spacing-xxl);
  padding: var(--spacing-xl);
  border-radius: var(--border-radius);
  background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
  color: var(--white);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
  align-items: flex-start;
`;

const ActionText = styled.p`
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0;
`;

const ActionButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--white);
  color: var(--primary);
  padding: 0.95rem 1.5rem;
  border-radius: var(--border-radius);
  font-weight: 700;
  text-decoration: none;
  transition: var(--transition);

  &:hover {
    background: var(--secondary);
    color: var(--white);
  }
`;

export default BlogDetailPage;
